import { Action } from '@/_core/fluxstore/types';
import { Activity, Persona, StorymapState, ViewType, defaultViewOptions } from '../types';
import { useStorymapStore } from '../useModule';
import { mapArrayDtoPropsToCamelCase, mapDtoPropsToCamelCase } from '@/_core/utils/mapDtos';
import { queryExecutor } from '@/_core/cqrs';
import { ActivityModel, PublishedStorymapQuery, StorymapQuery, StorymapQueryResult, PublishedStorymapQueryResult } from '../../api/queries';
import { initTaskRelease, refreshTaskReleases } from '../use/useTaskRelease';
import { goToExternalUrl } from '@/_core/utils/navigation';
import { SetCurrentViewType } from './viewSettings/setCurrentViewType';
import { useLocalStorage } from 'vue-composable';
import { FeatureAddOn, subscriptionManager } from '@/_core/utils/features';
import { parseIssueTypeAbbreviations } from '../../utils/issueTypeAbbreviations';
import { PublishedStorymapAccessKind } from '../../utils/publishedStorymapRoutes';
import { applyPublishedStorymapResult } from './initPublishedStorymap';

import dateFormat from 'date-fns/format';

const { registerReducer, registerMiddleware, dispatch } = useStorymapStore();

export class InitStorymap extends Action<StorymapState> {
    public static ACTION_TYPE = 'InitStorymap';
    public _res: StorymapQueryResult = null;
    public _publishedRes: PublishedStorymapQueryResult = null;

    public constructor(
        public slug: string,
        public cardId: string,
        public viewType: 'discovery' | 'planning' | 'backlog' | 'classic' | 'timeline' | 'tracking' | null,
        public trackingReleaseId: string | null = null,
        public ignoreViewType: boolean = false,
        public preferredTrackingCardId: string | null = null,
        public isCurrent: () => boolean = () => true
    ) {
        super(InitStorymap.ACTION_TYPE);
    }
}

const middleware = async (state: StorymapState, action: InitStorymap): Promise<boolean> => {
    action._res = await queryExecutor.executeQuery(new StorymapQuery(action.slug), (error) => {
        if (!action.isCurrent()) return { isHandled: true, result: null };
        if (error?.response?.status === 401 || error?.response?.status === 403 || error?.response?.status === 404) {
            return { isHandled: true, result: null };
        }

        return { isHandled: false };
    });

    if (!action.isCurrent()) return false;
    if (!action._res) {
        action._publishedRes = await queryExecutor.executeQuery(
            new PublishedStorymapQuery(PublishedStorymapAccessKind.Public, action.slug),
            () => ({ isHandled: !action.isCurrent(), result: null })
        );
        if (!action.isCurrent()) return false;
        document.title = action._publishedRes.Name + ' story map - StoriesOnBoard';
        return true;
    }

    if (
        !action.ignoreViewType &&
        subscriptionManager.hasFeatureAddOn(FeatureAddOn.ClassicStorymap) &&
        ((action.viewType != 'discovery' &&
            action.viewType != 'planning' &&
            action.viewType != 'backlog' &&
            action.viewType != 'timeline' &&
            action.viewType != 'tracking' &&
            action._res.ViewType == ViewType.Classic) ||
            action.viewType == 'classic')
    ) {
        if (action.cardId) {
            goToExternalUrl(`/m/${action.slug}/!card/${action.cardId}`);
        } else {
            goToExternalUrl('/m/' + action.slug);
        }
        return false;
    }

    document.title = action._res.Name + ' story map - StoriesOnBoard';

    return true;
};

const defultValuesOnFrontendModels = () => ({
    isActive: false,
    activeUsers: [] as string[],
    isMultiselected: false,
    isTitleEditing: false,
    isNew: false,
    isSaving: false,
    isCopied: false,
    isCut: false,
});

const mapActivities = (storymapId: string, serverSideModel: ActivityModel[]) => {
    const { storage: collapsedActivityIds } = useLocalStorage(`_storiesonboard_${storymapId}_collapsedActivityIds`, []);
    return serverSideModel.map(
        (x) =>
            ({
                ...mapCard(x),
                isCollapsed: collapsedActivityIds.value.includes(x.Id),
            } as Activity)
    );
};

const mapCards = (serverSideModel: any[]) => {
    const card = serverSideModel.map((x) => mapCard(x));

    return card;
};

const mapCard = (serverSideModel: any) => {
    return {
        ...mapDtoPropsToCamelCase(serverSideModel),
        ...defultValuesOnFrontendModels(),
        createdAt: new Date(serverSideModel.CreatedAt),
        lastUpdatedAt: new Date(serverSideModel.LastUpdatedAt),
    };
};

const reducer = (state: StorymapState, action: InitStorymap): void => {
    if (!action.isCurrent()) return;
    if (action._publishedRes) {
        applyPublishedStorymapResult(
            state,
            action._publishedRes,
            PublishedStorymapAccessKind.Public,
            action.slug,
            action.cardId,
            action.viewType,
            action.trackingReleaseId
        );
        return;
    }

    const res = action._res;

    state.id = res.Id;
    state.correlationId = res.Id;
    state.name = res.Name;
    state.slug = res.Slug;
    state.productSlug = res.ProductSlug;
    state.estimationUnit = res.EstimationUnit;
    state.aboutBoardDescription = res.AboutBoardDescription;
    state.showAboutBoard = true;
    state.aiData = mapDtoPropsToCamelCase(res.StorymapAiDatas);
    if (state.aiData && !state.aiData.language) {
        state.aiData.language = 'English';
    }
    state.isTemplate = res.IsTemplate;
    state.theme = res.Theme;
    state.activityColor = res.ActivityColor;
    state.taskColor = res.TaskColor;
    state.subtaskColor = res.SubtaskColor;
    state.cardKeyCounter = res.CardKeyCounter;
    state.cardKeyPrefix = res.CardKeyPrefix;
    state.isCardKeyEnabled = res.IsCardKeyEnabled;

    let viewType = null as ViewType;
    if (!action.ignoreViewType) {
        viewType = res.ViewType;
        if (action.viewType == 'discovery') {
            viewType = ViewType.Discovery;
        } else if (action.viewType == 'planning') {
            viewType = ViewType.Planning;
        } else if (action.viewType == 'backlog') {
            viewType = ViewType.Backlog;
        } else if (action.viewType == 'timeline') {
            viewType = ViewType.Timeline;
        } else if (action.viewType == 'tracking') {
            viewType = ViewType.Tracking;
        }

        state.currentViewType = viewType;
    } else {
        viewType = state.currentViewType;
    }

    state.discoveryViewOptions = { ...defaultViewOptions, ...mapDtoPropsToCamelCase(res.DiscoveryViewOptions), isShowAssignee: false };
    state.planningViewOptions = { ...defaultViewOptions, ...mapDtoPropsToCamelCase(res.PlanningViewOptions) };
    state.backlogViewOptions = { ...defaultViewOptions, ...mapDtoPropsToCamelCase(res.BacklogViewOptions) };

    state.prioFrameworkType = res.PrioFrameworkType;

    state.activities = mapActivities(state.id, res.Activities);
    state.tasks = mapCards(res.Tasks);
    state.subtasks = mapCards(res.Subtasks);

    state.annotations = mapArrayDtoPropsToCamelCase(res.Annotations);
    state.subtaskStatuses = mapArrayDtoPropsToCamelCase(res.SubtaskStatuses);

    state.customAiStoryTemplates = mapArrayDtoPropsToCamelCase(res.CustomAiStoryTemplates);
    state.customAiStoryTemplateExamples = mapArrayDtoPropsToCamelCase(res.CustomAiStoryTemplateExamples);
    state.isSampleStorymap = res.IsSampleStorymap ?? false;
    state.aiTemplateOverrides = mapArrayDtoPropsToCamelCase(
        res.StorymapAiTemplateOverrides ?? []
    );

    const { storage: collapsedReleaseIds } = useLocalStorage(`_storiesonboard_${state.id}_collapsedReleaseIds`, []);

    state.releases = res.Releases.map((x) => ({
        ...mapDtoPropsToCamelCase(x),
        startDate: x.StartDate == null ? null : dateFormat(new Date(x.StartDate), 'yyyy-MM-dd'),
        releaseDate: x.ReleaseDate == null ? null : dateFormat(new Date(x.ReleaseDate), 'yyyy-MM-dd'),
        isPinned: false,
        isCollapsed: collapsedReleaseIds.value.includes(x.Id),
        isFocused: false,
    }));

    state.releases.push({
        id: null,
        goal: '',
        isPinned: false,
        isCollapsed: collapsedReleaseIds.value.includes(null),
        prefix: '',
        name: 'Unscheduled cards',
        isArchived: false,
        order: 10000,
        isFocused: false,
        isNew: false,
    });

    const { storage: hasAlreadyPinnedRelease } = useLocalStorage('_storiesonboard_hasAlreadyPinnedRelease', false);
    state.hasAlreadyPinnedRelease = hasAlreadyPinnedRelease.value;

    const { storage: storedZoomLevel } = useLocalStorage<1 | 2 | 3>(`_storiesonboard_${state.id}_zoomLevel`, 2);
    state.zoomLevel = storedZoomLevel.value;

    state.colors = mapArrayDtoPropsToCamelCase(res.Colors);
    state.personas = mapArrayDtoPropsToCamelCase(res.Personas).map((x: Persona) => ({
        ...x,
        customFields: mapArrayDtoPropsToCamelCase(x.customFields),
        personalities: mapArrayDtoPropsToCamelCase(x.personalities),
        isNew: false,
    }));
    state.personaPictures = mapArrayDtoPropsToCamelCase(res.PersonaPictures);
    state.defaultPersonaCustomFields = mapArrayDtoPropsToCamelCase(res.DefaultPersonaCustomFields);
    state.defaultPersonaPersonalities = mapArrayDtoPropsToCamelCase(res.DefaultPersonaPersonalities);

    state.canEdit = res.CanEdit;
    state.canAdminister = res.CanAdminister;
    state.canCopyPrivateLink = res.CanCopyPrivateLink;
    state.canCopyPrivateReleaseLink = res.CanCopyPrivateReleaseLink;
    state.isNextGenPrivateLinkSharingEnabled = res.IsNextGenPrivateLinkSharingEnabled;
    state.hasFeedbackPortalAccess = res.HasFeedbackPortalAccess;

    state.isIntegrationEnabled = res.IsIntegrationEnabled;
    state.isImportEnabled = res.IsImportEnabled;
    state.integrationToolType = res.IntegrationToolType;
    state.isWsLevelIntegrationOauth1 = res.IsWsLevelIntegrationOauth1;
    state.isDisabledWsLevelIntegration = res.IsDisabledWsLevelIntegration;
    state.isCorporateWsLevelIntegration = res.IsCorporateWsLevelIntegration;
    state.isMissingImpersonationToken = res.IsMissingImpersonationToken;
    state.canPushActivityLevel = res.CanPushActivityLevel;
    state.canPushTaskLevel = res.CanPushTaskLevel;
    state.hasImportableCards = res.HasImportableCards;
    state.issueTypeAbbreviations = parseIssueTypeAbbreviations(res.IssueTypeAbbreviations);

    state.shouldShowOnboarding = res.ShouldShowOnboarding;
    state.isWorkspaceVisible = res.IsWorkspaceVisible;
    state.canWorkspaceMembersComment = res.CanWorkspaceMembersComment;
    state.viewersCanCommentOnCards = res.ViewersCanCommentOnCards;

    state.collaborators = mapArrayDtoPropsToCamelCase(action._res.Collaborators);
    state.shouldOpenIntegrationTokenExpiredDialog = false;

    if (!state.isInitialized) {
        initTaskRelease();
    } else {
        refreshTaskReleases();
    }

    state.isInitialized = true;

    const effectiveTrackingReleaseId =
        viewType === ViewType.Tracking ? (action.ignoreViewType ? state.trackingViewState.releaseId : action.trackingReleaseId) : null;

    if (viewType === ViewType.Tracking && action.preferredTrackingCardId) {
        const preferredTrackingCard = state.subtasks.find(
            (subtask) => subtask.id === action.preferredTrackingCardId && subtask.releaseId === effectiveTrackingReleaseId
        );

        if (preferredTrackingCard) {
            preferredTrackingCard.isActive = true;
        }
    }

    dispatch(new SetCurrentViewType(viewType, effectiveTrackingReleaseId));
};

//@ts-ignore
registerMiddleware(InitStorymap.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(InitStorymap.ACTION_TYPE, reducer);
